import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { RouteFactory } from "@app/routePaths";
import BTLogo from "@assets/bt-icon-white.svg";
import NoAdSettings from "@components/Advertisement/NoAdSettings";
import GAData from "@components/GAData/GAData";
import { getIconSrc } from "@components/Icon/helpers";
import { IconTypeEnum } from "@components/Icon/types";
import MetaTags from "@components/MetaTags/MetaTags";
import useSendPulseGaData from "@features/pulse/hooks/useSendPulseGaData";
import {
  filteredPulseArticles,
  getPulseGaTitle,
  getPulseSeoData,
} from "@features/pulse/utils/helpers";
import type { PulsePageContent } from "@features/pulse/utils/types";
import { SanitizedPulseData } from "@features/pulse/utils/types";
import { useWindowSize } from "@hooks/useWindowSize";
import { contactFormUrl } from "@pages/Article/components/ArticlePurchaseLink/constants";
import { dayjsSingaporeTimezone } from "@util/constant";
import dayjs from "dayjs";

import PulseDesktopPage from "./PulseDesktopPage";
import PulseMobilePage from "./PulseMobilePage";

export default function PulsePageContent({
  data: { pulseArticles, pulseIndividualArticles },
}: PulsePageContent): React.ReactElement {
  const navigate = useNavigate();

  const [sortedPulseData, setSortedPulseData] = useState<SanitizedPulseData[]>(
    []
  );
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [currentSlideId, setCurrentSlideId] = useState<string>("");

  const [firstLoad, setFirstLoad] = useState(true);

  const [backToBt, setBackToBt] = useState("");

  const { sendTrackingData } = useSendPulseGaData();
  const { isScreenLG } = useWindowSize();

  const handlePageChange = useCallback(
    (
      data: SanitizedPulseData[],
      activeId: string,
      route: string = RouteFactory.pulseSlide(activeId)
    ) => {
      setFirstLoad(false);
      setSortedPulseData(data);
      setCurrentSlideId(activeId);

      navigate(route);

      sendTrackingData("", "", activeId, data);
    },
    [navigate, sendTrackingData]
  );

  useEffect(() => {
    if (pulseArticles.length === 0) return;
    if (!firstLoad) return;

    const path = sessionStorage.getItem("pulse_back_path") || "/";
    setBackToBt(path);

    const firstArticle = pulseIndividualArticles?.[0];
    const activeId = firstArticle?.id || "";

    const withArticleId = pulseArticles.find(
      (article) => article.id === activeId
    );

    const data = withArticleId
      ? filteredPulseArticles(activeId, pulseArticles)
      : activeId !== "" && !withArticleId
        ? [...(pulseIndividualArticles || []), ...pulseArticles]
        : pulseArticles;

    const firstArticleInFullList = pulseArticles?.[0];

    const route =
      withArticleId || (activeId !== "" && !withArticleId)
        ? RouteFactory.pulseSlide(activeId)
        : RouteFactory.pulseSlide(firstArticleInFullList.id);

    const displayArticleId =
      activeId === "" ? firstArticleInFullList.id : activeId;

    handlePageChange(data, displayArticleId, route);
  }, [firstLoad, pulseArticles, pulseIndividualArticles, handlePageChange]);

  const firstArticle = sortedPulseData.filter(
    (article) => article.id === currentSlideId
  )?.[0];

  const {
    title: metaTitle,
    description: metaDesc,
    slug: metaSlug,
    imageUrl: metaImage,
  } = getPulseSeoData(pulseIndividualArticles?.[0]);

  const gaTitle = getPulseGaTitle(currentSlide, sortedPulseData, metaTitle);

  return (
    <div
      className="flex min-h-svh w-full flex-col bg-[#121212]"
      data-testid="pulse-page-content"
    >
      <NoAdSettings />

      <GAData
        event="virtual_pv"
        level2="pulse"
        contentcat={1}
        contenttype={currentSlide === sortedPulseData.length ? "" : "1"}
        title={gaTitle}
        articleid={firstArticle?.id || ""}
        pubdate={
          currentSlide === sortedPulseData.length
            ? "(not set)"
            : dayjs(firstArticle?.published)
                .tz(dayjsSingaporeTimezone)
                .format("YYYY-MM-DD HH:mm:ss")
        }
        author={firstArticle?.authors || "(not set)"}
        gsChannels="(not set)"
      />

      <MetaTags
        title={metaTitle}
        description={metaDesc}
        ogDescription={metaDesc}
        ogType="website"
        slug={metaSlug}
        imageUrl={metaImage}
        imageAlt="Pulse"
      />

      <div className="relative z-20 hidden justify-between p-4 pb-0 lg:flex">
        <Link to={`${RouteFactory.home}?ref=pulse`} reloadDocument>
          <img src={BTLogo} width={200} height={18} alt="The Business Times" />
        </Link>

        <Link to={`${backToBt}?ref=pulse`} reloadDocument>
          <img
            src={getIconSrc(IconTypeEnum.closeWhite)}
            width={36}
            height={36}
            alt="close"
          />
        </Link>
      </div>

      {isScreenLG ? (
        <PulseDesktopPage
          sortedPulseData={sortedPulseData}
          currentSlide={currentSlide}
          setCurrentSlide={setCurrentSlide}
          setCurrentSlideId={setCurrentSlideId}
          backToBt={backToBt}
        />
      ) : (
        <PulseMobilePage
          sortedPulseData={sortedPulseData}
          currentSlide={currentSlide}
          setCurrentSlide={setCurrentSlide}
          setCurrentSlideId={setCurrentSlideId}
          currentSlideId={currentSlideId}
          backToBt={backToBt}
        />
      )}

      <div className="absolute bottom-6 right-6 z-10 hidden font-poppins text-4xs font-light tracking-2% text-gray-550 lg:block">
        AI-generated summary.{" "}
        <a
          target="_blank"
          href={contactFormUrl}
          className="underline-offset-4 hover:underline"
          rel="noreferrer"
        >
          Feedback.
        </a>
      </div>
    </div>
  );
}
